import React from 'react';
import { Box, Stepper, Step, StepLabel, Typography, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Store as StoreIcon,
  Inventory as InventoryIcon,
  Payment as PaymentIcon,
  Palette as PaletteIcon,
} from '@mui/icons-material';

// Estilo personalizado para o ícone do step
const ColorlibStepIconRoot = styled('div')<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ theme, ownerState }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#ccc',
  zIndex: 1,
  color: '#fff',
  width: 50,
  height: 50,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  ...(ownerState.active && {
    backgroundImage: 'linear-gradient(136deg, #667eea 0%, #764ba2 100%)',
    boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
  }),
  ...(ownerState.completed && {
    backgroundImage: 'linear-gradient(136deg, #56ab2f 0%, #a8e6cf 100%)',
  }),
}));

function ColorlibStepIcon(props: {
  active?: boolean;
  completed?: boolean;
  icon?: React.ReactNode;
}) {
  const { active = false, completed = false, icon } = props;

  return (
    <ColorlibStepIconRoot ownerState={{ completed, active }}>
      {icon}
    </ColorlibStepIconRoot>
  );
}

// Configuração dos steps
const steps = [
  {
    label: 'Informações Básicas',
    description: 'Configure os dados básicos da sua loja',
    icon: <StoreIcon />,
  },
  {
    label: 'Produtos',
    description: 'Adicione os produtos da sua loja',
    icon: <InventoryIcon />,
  },
  {
    label: 'Pagamento',
    description: 'Configure os métodos de pagamento',
    icon: <PaymentIcon />,
  },
  {
    label: 'Personalização',
    description: 'Personalize a aparência da sua loja',
    icon: <PaletteIcon />,
  },
];

interface StoreFormStepperProps {
  activeStep: number;
}

const StoreFormStepper: React.FC<StoreFormStepperProps> = ({ activeStep }) => {
  return (
    <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        Configuração da Loja
      </Typography>
      
      <Stepper activeStep={activeStep} alternativeLabel>
        {steps.map((step, index) => (
          <Step key={step.label}>
            <StepLabel
              StepIconComponent={ColorlibStepIcon}
              StepIconProps={{
                icon: step.icon,
              }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {step.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {step.description}
                </Typography>
              </Box>
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Indicador de progresso */}
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Etapa {activeStep + 1} de {steps.length}
        </Typography>
        <Box
          sx={{
            width: '100%',
            height: 4,
            backgroundColor: 'grey.200',
            borderRadius: 2,
            mt: 1,
          }}
        >
          <Box
            sx={{
              width: `${((activeStep + 1) / steps.length) * 100}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 2,
              transition: 'width 0.3s ease-in-out',
            }}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default StoreFormStepper;
